import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Boxes, MapPinned, Send, Trash2, Trophy, ThumbsUp, Wrench, AlertTriangle, Plus, PackagePlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, fmtDate, numBR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const BITOLAS = ['1/4"', '3/8"', '1/2"', '5/8"', '3/4"', '7/8"'];
const CAT_NAMES = { cobre: "Cobre", isolamento: "Isolamento", cabo_pp: "Cabo PP", fixacao: "Fixação", acabamento: "Acabamento", refrigerante: "Refrigerantes", outros: "Outros" };

export default function Materials() {
  const { company } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rep, setRep] = useState(null);
  const [mine, setMine] = useState([]);
  const [consult, setConsult] = useState({ product_id: "", state: company.state || "", city: company.city || "" });
  const [result, setResult] = useState(null);
  const [voted, setVoted] = useState(false);
  const [contrib, setContrib] = useState({ product_id: "", price: "", state: company.state || "", city: company.city || "", purchase_date: "", supplier: "", notes: "" });
  const [newProduct, setNewProduct] = useState(null);
  const [accept, setAccept] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [kit, setKit] = useState({ metros: "3", bitola_liquido: '1/4"', bitola_gas: '1/2"', vias: "4", mm2: "1,5" });
  const [kitResult, setKitResult] = useState(null);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products/reputation/me").then((r) => setRep(r.data)).catch(() => {});
    api.get("/products/contributions/mine").then((r) => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const doConsult = async () => {
    if (!consult.product_id || !consult.state || !consult.city) {
      toast.error("Selecione o material e informe estado e cidade.");
      return;
    }
    setVoted(false);
    try {
      const { data } = await api.get(`/products/${consult.product_id}/reference`, { params: { state: consult.state, city: consult.city } });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const voteUseful = async () => {
    try {
      const { data } = await api.post(`/products/references/${result.ref_id}/useful`);
      toast.success(data.message);
      setVoted(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitContrib = async (e) => {
    e.preventDefault();
    if (!accept) {
      toast.error("Aceite os Termos de Contribuição de Dados.");
      return;
    }
    setSending(true);
    try {
      if (newProduct) {
        await api.post("/products", newProduct);
        toast.success("Material enviado para validação!");
        setNewProduct(null);
      } else {
        const { data } = await api.post(`/products/${contrib.product_id}/prices`, { ...contrib, price: numBR(contrib.price) });
        toast.success(data.message);
      }
      setContrib({ ...contrib, price: "", notes: "" });
      setAccept(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const removeMine = async (id) => {
    if (!window.confirm("Excluir esta contribuição?")) return;
    await api.delete(`/products/contributions/${id}`);
    toast.success("Contribuição excluída.");
    load();
  };

  const calcKit = async () => {
    try {
      const { data } = await api.get("/products/split-kit", { params: { ...kit, metros: numBR(kit.metros) } });
      setKitResult(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendToQuote = () => {
    localStorage.setItem("gepr_kit", JSON.stringify(kitResult.items.map((i) => ({ name: `${i.name} (${i.nota || i.unit})`, qty: i.qty, cost: 0 }))));
    toast.success("Kit enviado! Abra a Calculadora de orçamento e toque em 'Importar kit' na etapa de peças.");
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5 animate-in-up max-w-3xl" data-testid="materials-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            Materiais e preços <Badge className="bg-amber-100 text-amber-700 border-amber-200">BETA</Badge>
          </h1>
          <p className="text-sm text-slate-500">Base colaborativa nacional — alimentada pelos profissionais</p>
        </div>
        {rep && (
          <Card className="border-amber-200 bg-amber-50" data-testid="reputation-card">
            <CardContent className="p-3 flex items-center gap-3">
              <Trophy className="h-6 w-6 text-amber-500" />
              <div>
                <p className="font-mono-num font-semibold text-sm">{rep.points} pts · {rep.level}</p>
                <p className="text-[11px] text-slate-500">
                  {rep.next_level ? `Faltam ${rep.next_level.min - rep.points} pts para ${rep.next_level.name}` : "Nível máximo!"}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Tabs defaultValue="consultar">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="consultar" data-testid="mat-tab-consult">Consultar preço</TabsTrigger>
          <TabsTrigger value="contribuir" data-testid="mat-tab-contribute">Contribuir</TabsTrigger>
          <TabsTrigger value="kit" data-testid="mat-tab-kit">Kit instalação split</TabsTrigger>
          <TabsTrigger value="base" data-testid="mat-tab-base">Base de materiais</TabsTrigger>
          <TabsTrigger value="minhas" data-testid="mat-tab-mine">Minhas contribuições ({mine.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="consultar" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5 space-y-3">
              <div className="space-y-1.5">
                <Label>Material</Label>
                <Select value={consult.product_id} onValueChange={(v) => setConsult({ ...consult, product_id: v })}>
                  <SelectTrigger className="h-12" data-testid="mc-product"><SelectValue placeholder="Selecione o material" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>UF</Label>
                  <Select value={consult.state} onValueChange={(v) => setConsult({ ...consult, state: v })}>
                    <SelectTrigger className="h-12" data-testid="mc-state"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Cidade</Label>
                  <Input className="h-12" data-testid="mc-city" value={consult.city} onChange={(e) => setConsult({ ...consult, city: e.target.value })} />
                </div>
              </div>
              <Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={doConsult} data-testid="mc-consult-btn">
                <MapPinned className="h-4 w-4 mr-2" /> Consultar referência
              </Button>

              {result && (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-2" data-testid="mc-result">
                  {result.product?.safety_note && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 text-sm text-red-800" data-testid="mc-safety">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {result.product.safety_note}
                    </div>
                  )}
                  {result.forming ? (
                    <div>
                      <p className="font-semibold text-amber-800">{result.message}</p>
                      <p className="text-sm text-slate-600 mt-1">{result.cta} — contribua na aba <b>Contribuir</b> e ganhe pontos.</p>
                      <p className="text-xs text-slate-500 mt-1">{result.count} de {result.min_contributions} contribuições mínimas.</p>
                    </div>
                  ) : (
                    <>
                      <p className="font-semibold">{result.product.name} — {consult.city}/{consult.state}</p>
                      <div className="flex flex-wrap gap-6">
                        <div>
                          <p className="text-xs text-slate-500">Faixa de referência</p>
                          <p className="font-mono-num text-xl font-semibold">{brl(result.range[0])} – {brl(result.range[1])}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Mediana /{result.product.unit}</p>
                          <p className="font-mono-num text-xl font-semibold text-sky-700">{brl(result.median)}</p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">{result.count} contribuições · confiança {result.confidence} · atualizado em {result.updated_at}</p>
                      <p className="text-xs text-slate-500 italic">{result.disclaimer}</p>
                      <Button variant="outline" className="h-11" onClick={voteUseful} disabled={voted} data-testid="mc-useful-btn">
                        <ThumbsUp className="h-4 w-4 mr-2" /> {voted ? "Obrigado pelo voto!" : "Esta referência foi útil"}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contribuir" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Ajude o GEPR a conhecer sua região</CardTitle>
              <p className="text-xs text-slate-500">Informe um preço que você pagou ou viu. Dados agregados e anonimizados — ninguém vê seu valor individual.</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitContrib} className="space-y-3">
                {!newProduct ? (
                  <div className="space-y-1.5">
                    <Label>Material *</Label>
                    <Select value={contrib.product_id} onValueChange={(v) => setContrib({ ...contrib, product_id: v })} required>
                      <SelectTrigger className="h-12" data-testid="mcc-product"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}</SelectContent>
                    </Select>
                    <button type="button" className="text-xs text-sky-700 hover:underline" onClick={() => setNewProduct({ name: "", category: "outros", unit: "unidade" })} data-testid="mcc-new-product-link">
                      Não achou o material? Sugerir novo
                    </button>
                  </div>
                ) : (
                  <div className="border border-sky-200 bg-sky-50 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-semibold text-sky-900">Sugerir novo material</p>
                    <Input className="h-11 bg-white" placeholder="Nome do material *" data-testid="mcc-np-name" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} required />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={newProduct.category} onValueChange={(v) => setNewProduct({ ...newProduct, category: v })}>
                        <SelectTrigger className="h-11 bg-white" data-testid="mcc-np-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="h-11 bg-white" placeholder="Unidade (m, kg, barra)" data-testid="mcc-np-unit" value={newProduct.unit} onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })} />
                    </div>
                    <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => setNewProduct(null)} data-testid="mcc-np-cancel">← Voltar para materiais existentes</button>
                  </div>
                )}
                {!newProduct && (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label>UF *</Label>
                        <Select value={contrib.state} onValueChange={(v) => setContrib({ ...contrib, state: v })} required>
                          <SelectTrigger className="h-12" data-testid="mcc-state"><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 space-y-1.5">
                        <Label>Cidade *</Label>
                        <Input className="h-12" data-testid="mcc-city" required value={contrib.city} onChange={(e) => setContrib({ ...contrib, city: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Preço por unidade (R$) *</Label>
                        <Input className="h-12 font-mono-num" type="text" inputMode="decimal" required placeholder="Ex: 85,90" data-testid="mcc-price" value={contrib.price} onChange={(e) => setContrib({ ...contrib, price: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Data da compra</Label>
                        <Input className="h-12" type="date" data-testid="mcc-date" value={contrib.purchase_date} onChange={(e) => setContrib({ ...contrib, purchase_date: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Fornecedor (opcional)</Label>
                      <Input className="h-12" data-testid="mcc-supplier" value={contrib.supplier} onChange={(e) => setContrib({ ...contrib, supplier: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Observações</Label>
                      <Textarea rows={2} data-testid="mcc-notes" value={contrib.notes} onChange={(e) => setContrib({ ...contrib, notes: e.target.value })} />
                    </div>
                  </>
                )}
                <div className="flex items-start gap-2.5">
                  <Checkbox id="mat-terms" checked={accept} onCheckedChange={setAccept} className="mt-0.5" data-testid="mcc-terms" />
                  <Label htmlFor="mat-terms" className="text-sm text-slate-600 font-normal leading-snug">
                    Confirmo que a informação é real e aceito os Termos de Contribuição de Dados (dados agregados e anonimizados).
                  </Label>
                </div>
                <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="mcc-submit-btn">
                  <Send className="h-4 w-4 mr-2" /> {sending ? "Enviando..." : "Enviar contribuição (+10 pts)"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kit" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Wrench className="h-4 w-4 text-sky-600" /> Kit de instalação split</CardTitle>
              <p className="text-xs text-slate-500">Sugestão de materiais pela metragem. Bitolas e seção de cabo: <b>informação a validar</b> — confirme no manual do fabricante.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Comprimento da tubulação (m)</Label>
                  <Input className="h-12 font-mono-num" type="text" inputMode="decimal" placeholder="Metragem exata, ex: 4,5" data-testid="kit-metros" value={kit.metros} onChange={(e) => setKit({ ...kit, metros: e.target.value })} />
                  <p className="text-xs text-slate-500">Informe a metragem exata de cada linha de cobre — sem arredondamento fixo de 3 m.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Tubo líquido</Label>
                  <Select value={kit.bitola_liquido} onValueChange={(v) => setKit({ ...kit, bitola_liquido: v })}>
                    <SelectTrigger className="h-12" data-testid="kit-bitola-liq"><SelectValue /></SelectTrigger>
                    <SelectContent>{BITOLAS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Tubo sucção</Label>
                  <Select value={kit.bitola_gas} onValueChange={(v) => setKit({ ...kit, bitola_gas: v })}>
                    <SelectTrigger className="h-12" data-testid="kit-bitola-gas"><SelectValue /></SelectTrigger>
                    <SelectContent>{BITOLAS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Cabo PP — vias</Label>
                  <Select value={kit.vias} onValueChange={(v) => setKit({ ...kit, vias: v })}>
                    <SelectTrigger className="h-12" data-testid="kit-vias"><SelectValue /></SelectTrigger>
                    <SelectContent>{["2", "3", "4", "5", "6"].map((v) => <SelectItem key={v} value={v}>{v} vias</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Seção (mm²)</Label>
                  <Select value={kit.mm2} onValueChange={(v) => setKit({ ...kit, mm2: v })}>
                    <SelectTrigger className="h-12" data-testid="kit-mm2"><SelectValue /></SelectTrigger>
                    <SelectContent>{["1,5", "2,5", "4"].map((v) => <SelectItem key={v} value={v}>{v} mm²</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={calcKit} data-testid="kit-calc-btn">Montar kit</Button>
              {kitResult && (
                <div className="space-y-2" data-testid="kit-result">
                  {kitResult.items.map((i, x) => (
                    <div key={x} className="flex justify-between border-b border-slate-100 pb-1.5 text-sm" data-testid={`kit-item-${x}`}>
                      <span>{i.name}</span>
                      <span className="font-mono-num font-semibold">{i.qty} {i.unit}{i.qty > 1 && ["barra", "rolo", "par"].includes(i.unit) ? "s" : i.unit === "metro" ? "s" : ""}</span>
                    </div>
                  ))}
                  {kitResult.avisos.map((a, i) => (
                    <p key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">{a}</p>
                  ))}
                  <Button className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white" onClick={sendToQuote} data-testid="kit-to-quote-btn">
                    <PackagePlus className="h-4 w-4 mr-2" /> Enviar kit para o orçamento
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="base" className="mt-4 space-y-3">
          <div className="relative">
            <Input className="h-12" placeholder="Buscar material..." data-testid="mat-search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {Object.entries(CAT_NAMES).map(([cat, label]) => {
            const items = filtered.filter((p) => p.category === cat);
            if (!items.length) return null;
            return (
              <div key={cat}>
                <h2 className="text-sm font-semibold text-slate-700 mb-2">{label}</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {items.map((p) => (
                    <Card key={p.id} className="border-slate-200" data-testid={`mat-card-${p.id}`}>
                      <CardContent className="p-3.5">
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-slate-500">Unidade: {p.unit}{p.specs?.aplicacao ? ` · ${p.specs.aplicacao}` : ""}</p>
                        {p.safety_note && <p className="text-xs text-red-700 mt-1 flex gap-1"><AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />{p.safety_note}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="minhas" className="mt-4 space-y-2">
          {mine.length === 0 && <p className="text-sm text-slate-500">Você ainda não contribuiu com preços de materiais.</p>}
          {mine.map((c) => (
            <Card key={c.id} className="border-slate-200" data-testid={`my-pc-${c.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{c.product_name}</p>
                  <p className="text-xs text-slate-500">{c.city}/{c.state} · {fmtDate(c.created_at)}</p>
                  {c.status === "em_analise" && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 mt-1">Em análise</Badge>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono-num font-semibold">{brl(c.price)}</span>
                  <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 hover:bg-red-50" onClick={() => removeMine(c.id)} data-testid={`my-pc-del-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
